import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { XeroLogo } from '@/components/integrations/XeroButton'
import {
  providerLabel,
  useAccountingIntegrationFeature,
} from '@/services/api/integrations/accountingProviders'
import VaultMappingRow from '@/components/integrations/mapping/VaultMappingRow'
import AccountCategoryRow from '@/components/integrations/mapping/AccountCategoryRow'
import {
  type AccountClass,
  type Direction,
  type XeroAccount,
  type XeroMappingOverview,
  currentMapping,
  useRecategorisePending,
  useRefreshXeroReferences,
  useXeroMapping,
} from '@/services/api/integrations/useXeroMapping'

const PRIMARY: Record<Direction, AccountClass[]> = {
  in: ['income'],
  out: ['expense'],
}
const SECONDARY: AccountClass[] = ['liability', 'asset', 'equity', 'other']

const needsCategory = (account: XeroAccount, direction: Direction) =>
  account.is_active &&
  account.has_transactions &&
  !currentMapping(account.category_mappings, direction)

/**
 * Settings → Integrations → Account & Contact Mapping, for the Space's
 * accounting connection (Xero or Zoho Books).
 */
export default function XeroMappingPage() {
  const navigate = useNavigate()
  const { hasFeature, loaded: featuresLoaded } =
    useAccountingIntegrationFeature()
  const { data: overview } = useXeroMapping({
    enabled: featuresLoaded && hasFeature,
  })
  const provider = overview?.connection?.provider

  return (
    <div className="mx-auto min-h-screen max-w-4xl px-4 pb-10 pt-4">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {provider === 'xero' && <XeroLogo className="h-8" />}
          <div>
            <h3 className="font-bold text-txt-secondary">Account mapping</h3>
            <p className="text-sm text-txt-secondary">
              Where transactions imported from {providerLabel(provider)} land in
              this Space
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => navigate('/account?section=integrations')}
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {!featuresLoaded ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !hasFeature ? (
        <Card className="p-6 text-sm">
          Accounting integrations are not included in this Space's plan.
        </Card>
      ) : (
        <MappingBody />
      )}
    </div>
  )
}

function MappingBody() {
  const navigate = useNavigate()
  const { data, isPending, isError } = useXeroMapping()
  const refresh = useRefreshXeroReferences()
  const recategorise = useRecategorisePending()

  if (isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>
  if (isError || !data) {
    return (
      <Card className="p-6 text-sm">
        Could not load the account mapping. Please try again.
      </Card>
    )
  }
  if (!data.connection) {
    return (
      <Card className="space-y-3 p-6 text-sm">
        <p>No accounting software is connected to this Space.</p>
        <Button
          variant="outline"
          onClick={() => navigate('/account?section=integrations')}
        >
          Go to Integrations
        </Button>
      </Card>
    )
  }

  const provider = providerLabel(data.connection.provider)
  const bankAccounts = data.accounts.filter(
    (a) =>
      a.account_class === 'bank' &&
      (a.is_active || a.vault_mappings.length > 0),
  )
  const unmappedBanks = bankAccounts.filter(
    (a) => a.is_active && a.vault_mappings.length === 0,
  ).length
  const unmappedIn = data.accounts.filter(
    (a) => a.account_class !== 'bank' && needsCategory(a, 'in'),
  ).length
  const unmappedOut = data.accounts.filter(
    (a) => a.account_class !== 'bank' && needsCategory(a, 'out'),
  ).length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          Connected to{' '}
          <strong>
            {data.connection.tenant_name ?? `${provider} organisation`}
          </strong>
        </p>
        {data.can.manage && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="md"
              onClick={() => refresh.mutate(undefined)}
              disabled={refresh.isPending}
            >
              <RefreshCw className="mr-1 size-4" /> Refresh from {provider}
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={() => recategorise.mutate(undefined)}
              disabled={recategorise.isPending}
              title="Apply the current mappings to imported transactions that are still pending review"
            >
              Re-categorise pending
            </Button>
          </div>
        )}
      </div>

      {data.references_refreshing && (
        <Card className="p-4 text-sm text-muted-foreground">
          Loading accounts and contacts from {provider}… this page updates by
          itself.
        </Card>
      )}

      <Card className="p-4 md:p-6">
        <Tabs defaultValue="banks">
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="banks">
              Bank accounts → Vaults <CountBadge count={unmappedBanks} />
            </TabsTrigger>
            <TabsTrigger value="in">
              Money In <CountBadge count={unmappedIn} />
            </TabsTrigger>
            <TabsTrigger value="out">
              Money Out <CountBadge count={unmappedOut} />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="banks">
            {bankAccounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No bank accounts found in {provider}.
              </p>
            ) : (
              <>
                <p className="mb-2 text-sm text-muted-foreground">
                  Transactions from a bank account without a vault are held back
                  until it is mapped.
                </p>
                {bankAccounts.map((account) => (
                  <VaultMappingRow
                    key={account.id}
                    account={account}
                    canEdit={data.can.map_vaults}
                  />
                ))}
              </>
            )}
          </TabsContent>
          <TabsContent value="in">
            <CategoryTab data={data} direction="in" />
          </TabsContent>
          <TabsContent value="out">
            <CategoryTab data={data} direction="out" />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  )
}

function CountBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span
      className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white"
      aria-label={`${count} need mapping`}
    >
      {count}
    </span>
  )
}

function CategoryTab({
  data,
  direction,
}: {
  data: XeroMappingOverview
  direction: Direction
}) {
  const candidates = data.accounts.filter((a) => a.account_class !== 'bank')
  // Before the first import nothing is "used" yet, so start with everything visible.
  const anyUsed = candidates.some((a) => a.has_transactions)
  const [showUnused, setShowUnused] = useState(!anyUsed)
  const provider = providerLabel(data.connection?.provider)

  const visible = useMemo(
    () =>
      candidates.filter((a) => {
        const mapped = a.category_mappings.some(
          (m) => m.direction === direction,
        )
        if (!a.is_active && !mapped) return false
        return showUnused || a.has_transactions || mapped
      }),
    [candidates, direction, showUnused],
  )
  const primary = visible.filter((a) =>
    PRIMARY[direction].includes(a.account_class),
  )
  const secondary = visible.filter((a) => SECONDARY.includes(a.account_class))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {direction === 'in'
            ? `Money coming into a bank account takes the category mapped to the account it was coded to in ${provider}.`
            : `Money leaving a bank account takes the category mapped to the account it was coded to in ${provider}.`}{' '}
          Unmapped lines go to Unreconciled so balances stay right.
        </p>
        <label className="flex shrink-0 items-center gap-2 text-sm">
          <Switch checked={showUnused} onCheckedChange={setShowUnused} />
          Show unused accounts
        </label>
      </div>

      <AccountGroup
        title={direction === 'in' ? 'Income accounts' : 'Expense accounts'}
        accounts={primary}
        direction={direction}
        data={data}
        defaultOpen
      />
      <AccountGroup
        title="Other accounts (liabilities, assets, equity…)"
        accounts={secondary}
        direction={direction}
        data={data}
        defaultOpen={secondary.some((a) => needsCategory(a, direction))}
      />
    </div>
  )
}

function AccountGroup({
  title,
  accounts,
  direction,
  data,
  defaultOpen,
}: {
  title: string
  accounts: XeroAccount[]
  direction: Direction
  data: XeroMappingOverview
  defaultOpen: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const unmapped = accounts.filter((a) => needsCategory(a, direction)).length

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="size-4" />
        ) : (
          <ChevronRight className="size-4" />
        )}
        {title}
        <span className="text-xs font-normal text-muted-foreground">
          ({accounts.length})
        </span>
        <CountBadge count={unmapped} />
      </button>
      {open && (
        <div className="px-3">
          {accounts.length === 0 ? (
            <p className="pb-3 text-sm text-muted-foreground">
              No accounts to show.
            </p>
          ) : (
            accounts.map((account) => (
              <AccountCategoryRow
                key={account.id}
                account={account}
                direction={direction}
                contacts={data.contacts}
                canEdit={data.can.map_categories}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}
